import { FOCUS_RING_NO_RADIUS, Icon, TextInput } from '@amelio/ui-library';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import KeyResultResponsibleSelect from './KeyResultResponsibleSelect';
import { KEY_RESULT_UNIT_ICON } from './keyResultUnitIcon';
import { IconCircle } from './surfaces';
import { TITLE_MAX_LENGTH } from '../validation';

import type { EmployeeOption } from '../hooks/useKeyResultResponsibles';
import type { ObjectiveFormData } from '../validation';
import type { KeyResultUnitValue } from '@/constants/KeyResultUnit';
import type { Control } from 'react-hook-form';

import { progressBadgeClassName } from '@/components/objectives/objectiveStyles';
import { formatPercent } from '@/utils/numberFormat';
import { validationMessageKey } from '@/utils/validationMessage';

/** The name reads as the card's title, not as a boxed field: an underline replaces the frame
 * `TextInput` draws around it, always there so a filled name still shows it can be edited. */
const TITLE_FIELD_CLASS =
    'text-navy h-auto rounded-none border-0 border-b bg-transparent px-1 py-0.5 text-[16px] leading-[24px] font-medium';
const TITLE_UNDERLINE = {
    idle: 'border-obj-line hover:border-primary focus-within:border-primary',
    error: 'border-error focus-within:border-error',
};

interface KeyResultCardHeaderProps {
    control: Control<ObjectiveFormData>;
    index: number;
    unit: KeyResultUnitValue | undefined;
    isTeam: boolean;
    employeeOptions: EmployeeOption[];
    canRemove: boolean;
    onRemove: () => void;
    displayPercentage: number;
    language: string;
}

/** Who the measurement is and who owns it: its glyph, its name, its responsible, its bin and the
 * badge that reads where it stands. */
export default function KeyResultCardHeader({
    control,
    index,
    unit,
    isTeam,
    employeeOptions,
    canRemove,
    onRemove,
    displayPercentage,
    language,
}: KeyResultCardHeaderProps) {
    const { t } = useTranslation();

    return (
        <div className={'mb-4 flex items-center justify-between gap-3'}>
            <div className={'flex min-w-0 flex-1 items-center gap-3'}>
                <IconCircle className={'bg-obj-tint h-10 w-10'}>
                    {unit && (
                        <Icon
                            name={KEY_RESULT_UNIT_ICON[unit]}
                            size={'sm'}
                            colorClassName={'text-navy'}
                        />
                    )}
                </IconCircle>

                <div className={'min-w-0 flex-1'}>
                    <Controller
                        name={`keyResults.${index}.title`}
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextInput
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                placeholder={t('objectives.form.keyResultNamePlaceholder')}
                                maxLength={TITLE_MAX_LENGTH}
                                showCharacterCount
                                error={!!fieldState.error}
                                errorMessage={
                                    fieldState.error?.message
                                        ? t(validationMessageKey(fieldState.error.message), {
                                              max: TITLE_MAX_LENGTH,
                                          })
                                        : undefined
                                }
                                className={`${TITLE_FIELD_CLASS} ${
                                    TITLE_UNDERLINE[fieldState.error ? 'error' : 'idle']
                                }`}
                                dataTestId={`key-result-${index}-title`}
                            />
                        )}
                    />
                </div>
            </div>

            <div className={'flex flex-shrink-0 items-center gap-3'}>
                {isTeam && (
                    <Controller
                        name={`keyResults.${index}.responsibleEmployeeId`}
                        control={control}
                        render={({ field }) => (
                            <KeyResultResponsibleSelect
                                options={employeeOptions}
                                value={field.value}
                                onChange={field.onChange}
                                dataTestId={`key-result-${index}-responsible`}
                            />
                        )}
                    />
                )}

                {/* Absent rather than disabled when it is the only measurement: an objective
                    always keeps at least one, so a bin that can never be used is an offer the
                    form does not honour. */}
                {canRemove && (
                    <button
                        type={'button'}
                        onClick={onRemove}
                        className={`text-obj-stepper rounded-md p-1.5 opacity-0 transition-opacity group-focus-within/card:opacity-100 group-hover/card:opacity-100 group-hover/card:hover:opacity-70 ${FOCUS_RING_NO_RADIUS}`}
                        aria-label={t('objectives.form.removeKeyResult')}
                        data-testid={`key-result-${index}-remove`}
                    >
                        <Icon
                            name={'essential-trash'}
                            size={'sm'}
                            color={'inherit'}
                        />
                    </button>
                )}

                <span
                    className={`${progressBadgeClassName(displayPercentage)} text-[12px]`}
                    data-testid={`key-result-${index}-percentage`}
                >
                    {formatPercent(displayPercentage, language)}
                </span>
            </div>
        </div>
    );
}
